import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import DefaultStatisticsCard from "./components/RecvCard";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCustomerValue } from "providers/CustomerContext";
import { getSalesDashboardStatusCountByCustomer } from "services/data.dashboard.service";
import { ListLayout } from "ui-components/Layout";
import { TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import MDButton from "components/MDButton";
import { SelectFields } from "ui-components/Form";
import { LoadingDialog } from "ui-components/Alert";
import { LoadingAlert } from "ui-components/Alert";
import { getSalesDashboardStatusCountByCustomer2 } from "services/data.dashboard.service";

function MainDashboard2() {
  const { customerOpt } = useCustomerValue();

  const enabled = Boolean(customerOpt.length);

  let currentDate = new Date();
  const [dateEnd, setDateEnd] = useState(new Date(currentDate));
  const [dateStart, setDateStart] = useState(
    new Date(currentDate.setDate(currentDate.getDate() - 7))
  );

  const [tabs, setTabs] = useState([
    { label: "No Stock", count: 0 },
    { label: "Unassign / In Picking", count: 0 },
    { label: "Packing", count: 0 },
    { label: "Awaiting Dispatch", count: 0 },
    { label: "Out For Delivery", count: 0 },
    { label: "Completed", count: 0 }
  ]);

  const [tableData, setTableData] = useState([]);
  const [filterMethod, setFilterMethod] = useState("Order Date");

  const { isFetching, refetch } = useQuery({
    queryKey: ["sales-status-by-customers", customerOpt],
    enabled: enabled,
    queryFn: ({ signal }) =>
      // getSalesDashboardStatusCountByCustomer(signal, {
      getSalesDashboardStatusCountByCustomer2(signal, {
        filterMethod: filterMethod,
        dateStart: Boolean(dateStart) ? dayjs(dateStart).format("YYYY-MM-DD") : null,
        dateEnd: Boolean(dateEnd) ? dayjs(dateEnd).format("YYYY-MM-DD") : null,
        customers: customerOpt,
      }),
    onSuccess: (data) => {
      if (data.data.data) {
        const result = data.data.data;

        const statusTotals = result.reduce((acc, obj) => {
          obj.status.forEach((s) => {
            const existing = acc.find((item) => item.label === s.label);
            if (existing) {
              existing.count += s.count;
            } else {
              acc.push({ label: s.label, count: s.count });
            }
          });
          return acc;
        }, []);

        setTabs(statusTotals);

        const flattenedData = result.map((obj) => {
          const flattened = { customerCode: obj.customerCode, totalQuantity: obj.totalQuantity };
          var total = 0;
          obj.status.forEach((s) => {
            flattened[s.label] = s.count;
            total += s.count;
          });
          flattened["total"] = total;
          return flattened;
        });

        setTableData(flattenedData);
      }
      else{
        setTableData([])
        setTabs([
          { label: "No Stock", count: 0 },
          { label: "Unassign / In Picking", count: 0 },
          { label: "Packing", count: 0 },
          { label: "Awaiting Dispatch", count: 0 },
          { label: "Out For Delivery", count: 0 },
          { label: "Completed", count: 0 }
        ])
      }
    },
  });

  const columns = [
    {
      header: "Customer Code",
      accessorKey: "customerCode",
      footer: "Total",
    },
    {
      header: "No Stock",
      accessorFn: (row) => row["No Stock"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("No Stock").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Unassign / In Picking",
      accessorFn: (row) => row["Unassign / In Picking"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Unassign / In Picking").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Packing",
      accessorFn: (row) => row["Packing"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Packing").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Awaiting Dispatch",
      accessorFn: (row) => row["Awaiting Dispatch"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Awaiting Dispatch").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Out For Delivery",
      accessorFn: (row) => row["Out For Delivery"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Out For Delivery").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Completed",
      accessorFn: (row) => row["Completed"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Completed").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Total",
      accessorFn: (row) => row["total"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Total").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "Total Quantity",
      accessorFn: (row) => row["totalQuantity"].toLocaleString(),
      footer: ({ table }) =>
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + parseInt(row.getValue("Total Quantity").replace(/[^0-9.-]+/g, "")), 0).toLocaleString(),
    },
    {
      header: "% Completed",
      accessorKey: "completedperc",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const {
          "Awaiting Dispatch": awaitDispatch,
          "Out For Delivery": outDelivery,
          "Completed": completed,
          total,
        } = row.original;
        const complete = total !== 0 ? (awaitDispatch + outDelivery + completed) / total : 0;
        let bgcolor = "rgba(255,255,255,0)"; //white
        if (complete > 0 && complete <= 0.5) {
          bgcolor = "rgba(255,0,0,0.5)"; //red
        } else if (complete > 0.5 && complete <= 0.8) {
          bgcolor = "rgba(255,165,0,0.5)"; //orang
        } else if (complete > 0.8 && complete <= 0.9) {
          bgcolor = "rgba(255,255,0,0.5)"; //yellow
        } else if (complete > 0.9) {
          bgcolor = "rgba(0,255,0,0.5)"; //green
        } else {
          bgcolor = "white";
        }
        const completeInPerc = complete * 100;
        const rounded = Math.round(completeInPerc * 10) / 10;
        return (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <div
              style={{
                backgroundColor: bgcolor,
                padding: "8px",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {rounded}%
            </div>
          </div>
        );
      },
      footer: ({ table }) => {
        const filteredRows = table.getFilteredRowModel().rows;
        const totalAwaitingDispatch = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Awaiting Dispatch").replace(/[^0-9.-]+/g, "")),
          0
        );
        const totalOutForDelivery = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Out For Delivery").replace(/[^0-9.-]+/g, "")),
          0
        );
        const totalOverall = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Total").replace(/[^0-9.-]+/g, "")),
          0
        );
        const totalCompleted = filteredRows.reduce(
          (total, row) => total + parseInt(row.getValue("Completed").replace(/[^0-9.-]+/g, "")),
          0
        );
        const complete =
          totalOverall !== 0 ? (totalAwaitingDispatch + totalOutForDelivery + totalCompleted) / totalOverall : 0;
        let bgcolor = "rgba(255,255,255,0)"; //white
        if (complete > 0 && complete <= 0.5) {
          bgcolor = "rgba(255,0,0,0.5)"; //red
        } else if (complete > 0.5 && complete <= 0.8) {
          bgcolor = "rgba(255,165,0,0.5)"; //orang
        } else if (complete > 0.8 && complete <= 0.9) {
          bgcolor = "rgba(255,255,0,0.5)"; //yellow
        } else if (complete > 0.9) {
          bgcolor = "rgba(0,255,0,0.5)"; //green
        } else {
          bgcolor = "white";
        }
        const completedPercentage = complete * 100;
        const roundedPercentage = Math.round(completedPercentage * 10) / 10;
        return (
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <div
              style={{
                backgroundColor: bgcolor,
                padding: "8px",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {roundedPercentage}%
            </div>
          </div>
        );
      },
    },
  ];

  const customProps = {
    startDate: dateStart,
    setStartDate: setDateStart,
    endDate: dateEnd,
    setEndDate: setDateEnd,
  };

  return (
    <ListLayout lg={12}>
      {(isFetching) && <LoadingAlert />}
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="No Stock"
              count={tabs.find((x) => x.label === "No Stock")?.count ?? 0}
              color={"#fdfdc1"}
            />
          </Grid>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="Unassign / In Picking"
              count={tabs.find((x) => x.label === "Unassign / In Picking")?.count ?? 0}
              color={"lightcyan"}
            />
          </Grid>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="Packing"
              count={tabs.find((x) => x.label === "Packing")?.count ?? 0}
              color={"#C1ECFA"}
            />
          </Grid>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="Awaiting Dispatch"
              count={tabs.find((x) => x.label === "Awaiting Dispatch")?.count ?? 0}
              color={"lightyellow"}
            />
          </Grid>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="Out For Delivery"
              count={tabs.find((x) => x.label === "Out For Delivery")?.count ?? 0}
              color={"bisque"}
            />
          </Grid>
          <Grid item xs={2}>
            <DefaultStatisticsCard
              title="Completed"
              count={tabs.find((x) => x.label === "Completed")?.count ?? 0}
              color={"#bff8d9"}
            />
          </Grid>
        </Grid>
      </MDBox>

      <TableCard>
        <Grid container justifyContent="flex-end" margin="10px">
          <Grid item xs={10}>
            <Grid justifyContent="flex-end" container spacing={3} sx={{ width: "100%" }}>
              <Grid item xs={3}>
                <SelectFields
                  field={{ name: "", label: "Filter by Date", value: filterMethod, errors: {} }}
                  options={["Order Date", "Received On"]}
                  onChange={(e, v) => setFilterMethod(v)}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomDateRangePicker {...customProps} />
              </Grid>
              <Grid item>
                <MDButton
                  color="info"
                  disabled={!dateStart || !dateEnd || isFetching}
                  onClick={() => {
                    refetch();
                  }}
                >
                  search
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <BasicTable columns={columns} data={tableData} />
      </TableCard>
    </ListLayout>
  );
}

export default MainDashboard2;
